import { ChevronDown, ChevronUp, Pencil, Trash2, CheckCircle, XCircle, AlertCircle } from "lucide-react";
import { IconButton } from "../../primitives/IconButton";
import { Badge } from "../../primitives/Badge";
import { cn } from "../../utils/cn";
import type { ProcessActivityTemplate, ProcessTriggerTemplate } from "../../../api/processes";

type ProcessStepCardProps = {
  step: {
    id: string;
    title: string;
    description: string | null;
    order_index: number;
    sla_days: number | null;
    completion_policy: string;
  };
  onEdit?: () => void;
  onDelete?: () => void;
  expanded?: boolean;
  onToggle?: () => void;
  activities?: ProcessActivityTemplate[];
  triggers?: ProcessTriggerTemplate[];
  entryConditions?: any;
  exitConditions?: any;
};

export function ProcessStepCard({
  step,
  onEdit,
  onDelete,
  expanded = false,
  onToggle,
  activities = [],
  triggers = [],
  entryConditions,
  exitConditions,
}: ProcessStepCardProps) {
  const hasEntryConditions = entryConditions && (
    (entryConditions.all && entryConditions.all.length > 0) ||
    (entryConditions.any && entryConditions.any.length > 0)
  );
  const hasExitConditions = exitConditions && (
    (exitConditions.all && exitConditions.all.length > 0) ||
    (exitConditions.any && exitConditions.any.length > 0)
  );

  const getCompletionPolicyLabel = (policy: string) => {
    const labels: Record<string, string> = {
      all_required: "Toutes activités requises",
      manual_by_assignee: "Manuel par assigné",
      external_confirmation: "Confirmation externe",
      time_based: "Basé sur le temps",
    };
    return labels[policy] || policy;
  };

  return (
    <div className="border border-border rounded-lg bg-surface">
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-sm font-medium text-muted-foreground">
                Étape {step.order_index}
              </span>
              <h4 className="text-md font-semibold">{step.title}</h4>
              {step.sla_days && (
                <Badge label={`SLA: ${step.sla_days}j`} tone="info" />
              )}
              <Badge label={getCompletionPolicyLabel(step.completion_policy)} tone="neutral" />
            </div>
            {step.description && (
              <p className="text-sm text-muted-foreground mb-2">{step.description}</p>
            )}
            {(hasEntryConditions || hasExitConditions) && (
              <div className="flex gap-2 mt-2">
                {hasEntryConditions && (
                  <span className="text-xs px-2 py-0.5 bg-info/10 text-info rounded flex items-center gap-1">
                    <CheckCircle size={12} />
                    Conditions d'entrée
                  </span>
                )}
                {hasExitConditions && (
                  <span className="text-xs px-2 py-0.5 bg-warning/10 text-warning rounded flex items-center gap-1">
                    <AlertCircle size={12} />
                    Conditions de sortie
                  </span>
                )}
              </div>
            )}
          </div>
          <div className="flex gap-1">
            {onToggle && (
              <IconButton
                icon={expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                ariaLabel={expanded ? "Réduire" : "Développer"}
                intent="ghost"
                size="sm"
                onClick={onToggle}
              />
            )}
            {onEdit && (
              <IconButton
                icon={<Pencil size={16} />}
                ariaLabel="Modifier l'étape"
                intent="ghost"
                size="sm"
                onClick={onEdit}
              />
            )}
            {onDelete && (
              <IconButton
                icon={<Trash2 size={16} />}
                ariaLabel="Supprimer l'étape"
                intent="ghost"
                size="sm"
                onClick={onDelete}
              />
            )}
          </div>
        </div>
      </div>

      {expanded && (
        <div className="border-t border-border p-4 bg-surface-subtle space-y-4">
          {/* Activities */}
          <div>
            <h5 className="text-sm font-medium mb-2">Activités ({activities.length})</h5>
            {activities.length > 0 ? (
              <div className="space-y-2">
                {activities.map((activity) => (
                  <div
                    key={activity.id}
                    className="p-2 border border-border rounded bg-surface text-sm"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{activity.title}</span>
                      {activity.is_required && (
                        <Badge label="Requis" tone="warning" />
                      )}
                      <span className="text-xs text-muted-foreground">
                        {activity.activity_type}
                      </span>
                    </div>
                    {activity.description && (
                      <p className="text-xs text-muted-foreground mt-1">{activity.description}</p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Aucune activité</p>
            )}
          </div>

          {/* Triggers */}
          <div>
            <h5 className="text-sm font-medium mb-2">Triggers ({triggers.length})</h5>
            {triggers.length > 0 ? (
              <div className="space-y-2">
                {triggers.map((trigger) => (
                  <div
                    key={trigger.id}
                    className="p-2 border border-border rounded bg-surface text-sm"
                  >
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{trigger.event_type}</span>
                      <span className="text-xs text-muted-foreground">→</span>
                      <span className="text-xs text-muted-foreground">{trigger.action_type}</span>
                      {trigger.is_enabled !== false ? (
                        <Badge label="Actif" tone="success" />
                      ) : (
                        <Badge label="Désactivé" tone="neutral" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">Aucun trigger</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
